import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { queryNextAgendaEvent, type NextAgendaEvent } from "@/lib/nextAgendaEvent";
import { subscribeAgendaChanges } from "@/lib/agendaChanges";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";

type SessionState = { status: "loading" | "error" | "ready"; userId: string | null };
type AgendaState = {
  status: "loading" | "error" | "signed-out" | "ready";
  nextEvent: NextAgendaEvent | null;
};

/** Agenda recovery never reruns Home's profile, streak or generated hero. */
export function useHomeAgenda() {
  const [attempt, setAttempt] = useState(0);
  const [session, setSession] = useState<SessionState>({ status: "loading", userId: null });
  const [state, setState] = useState<AgendaState>({ status: "loading", nextEvent: null });
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const revision = useRef(0);
  const ownerRef = useRef<string | null>(null);
  const refresh = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    let active = true;
    let authRevision = 0;
    let owner: string | null | undefined;
    revision.current += 1;
    setCheckedAt(null);
    setState({ status: "loading", nextEvent: null });
    setSession({ status: "loading", userId: null });
    const applySession = (userId: string | null) => {
      if (owner === userId) return; // Same-user token refresh keeps the result.
      owner = userId; ownerRef.current = userId;
      revision.current += 1; // Invalidate in-flight reads synchronously on logout/account change.
      setCheckedAt(null);
      setState({ status: userId ? "loading" : "signed-out", nextEvent: null });
      setSession({ status: "ready", userId });
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, value) => {
      if (!active) return;
      authRevision += 1;
      applySession(value?.user.id ?? null);
    });
    const startedAt = authRevision;
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active || authRevision !== startedAt) return;
      if (error) setSession({ status: "error", userId: null });
      else applySession(data.session?.user.id ?? null);
    }).catch(() => {
      if (active && authRevision === startedAt) setSession({ status: "error", userId: null });
    });
    return () => { active = false; revision.current += 1; subscription.unsubscribe(); };
  }, [attempt]);

  useEffect(() => {
    if (session.status === "loading") return;
    if (session.status === "error" || !session.userId) {
      setState({ status: session.status === "error" ? "error" : "signed-out", nextEvent: null });
      return;
    }
    let active = true;
    const request = revision.current;
    const checked = Date.now();
    // Runs outside the synchronous auth callback, avoiding Supabase auth locks.
    const uid = session.userId;
    const assert = () => { if (!active || request !== revision.current || ownerRef.current !== uid) throw new Error("invalid_session"); };
    void (async () => {
      assert(); const user = await supabase.auth.getUser(); assert();
      if (user.error || user.data.user?.id !== uid) throw new Error("invalid_session");
      const auth = await supabase.auth.getSession(); assert();
      if (auth.error || auth.data.session?.user.id !== uid || !auth.data.session.access_token) throw new Error("invalid_session");
      const result = await queryNextAgendaEvent(uid, new Date(checked), { accessToken: auth.data.session.access_token, assertCurrent: assert }); assert();
      return result;
    })().then(({ data, error }) => {
      if (!active || request !== revision.current) return;
      if (!error) setCheckedAt(checked);
      setState(error ? { status: "error", nextEvent: null } : { status: "ready", nextEvent: data });
    }).catch(() => {
      if (active && request === revision.current) setState({ status: "error", nextEvent: null });
    });
    return () => { active = false; };
  }, [session]);

  useEffect(() => subscribeAgendaChanges(owner => { if (owner === ownerRef.current) refresh(); }), [refresh]);

  useAgendaAutoRefresh({
    checkedAt, nextEvent: state.nextEvent, loading: state.status === "loading",
    error: state.status === "error", enabled: state.status !== "signed-out", refresh,
  });

  return { ...state, refresh };
}
